import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
} from "react";

export type Language = "cs" | "sk";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (cs: string, sk: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Detect default language based on domain
const getDefaultLanguageFromDomain = (): Language => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.endsWith(".sk") || hostname.includes("curio-ai.sk")) {
      return "sk";
    }
  }
  return "cs"; // Default to Czech for .cz and other domains
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(getDefaultLanguageFromDomain);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language") as Language;
    if (storedLanguage === "cs" || storedLanguage === "sk") {
      setLanguage(storedLanguage);
    }
  }, []);

  const handleSetLanguage = useCallback((lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  }, []);

  const t = useCallback(
    (cs: string, sk: string): string => {
      switch (language) {
        case "cs":
          return cs;
        case "sk":
          return sk;
        default:
          return cs;
      }
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage: handleSetLanguage, t }),
    [language, handleSetLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
